/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable no-use-before-define */
import { ChangeEvent, FC } from 'react';
import { Divider, FormLabel, Radio, Switch, Wrapper } from '@screentone/core';

import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { MobileArticleAttributes, MobileArticleItem } from 'data/generated/graphql';
import { camelCaseToWords } from 'utils/case';
import { getContentType, getStoryType } from 'utils/mobile-content';
import style from './MobileArticleActions.module.scss';
import {
  Content,
  ContentTypeKeys,
  SizeConfigKeys,
  TreatmentConfig,
  TreatmentOption,
  TreatmentOptionConfig
} from './types';

const ENABLE_TREATMENTS = false; // Hidden but logic preserved for backwards compatibility

interface MobileArticleActionsProps {
  article: MobileArticleItem;
  hierarchyId: string;
  rulesConfig: Record<string, unknown> | null | undefined;
  isReadOnly?: boolean;
}

const MobileArticleActions: FC<MobileArticleActionsProps> = ({ article, hierarchyId, rulesConfig, isReadOnly }) => {
  const { modifyEntity } = useDataModelContext();

  const treatment = getStoryType({ item: article });
  const contentType = getContentType({ item: article });

  const config = rulesConfig ? (rulesConfig as unknown as Content) : undefined;

  const sizeConfigs = config ? config[treatment][contentType] : undefined;

  const articleSizeConfig = sizeConfigs ? sizeConfigs[article.attributes.size as ContentTypeKeys] : undefined;

  const hasErrorInConfig = !articleSizeConfig;

  const sizeConfigsForTreatments = !hasErrorInConfig ? Object.keys(articleSizeConfig) : [];

  const availableTreatmentsForSize = !hasErrorInConfig
    ? sizeConfigsForTreatments
        .filter((sizeConfig) => !!articleSizeConfig[sizeConfig as SizeConfigKeys])
        .map((sizeConfig) => ({
          key: sizeConfig,
          ...articleSizeConfig[sizeConfig as SizeConfigKeys]
        }))
    : [];

  const availableOptionsForTreatment = !hasErrorInConfig
    ? articleSizeConfig[article.attributes.treatment as SizeConfigKeys]
    : undefined;

  const handleTreatmentChange = (e: ChangeEvent<HTMLInputElement>, treatmentOption: TreatmentConfig) => {
    modifyEntity(hierarchyId, (entity) => {
      const article = entity as MobileArticleItem;
      article.attributes.treatment = e.target.value;

      if (treatmentOption.autoProperties) {
        Object.keys(treatmentOption.autoProperties).forEach((prop) => {
          const value = treatmentOption.autoProperties![prop as keyof MobileArticleAttributes];
          if (value) {
            // @ts-expect-error - TS doesn't like this, but it's fine
            article.attributes[prop as keyof MobileArticleAttributes] = value;
          }
        });
      }
    });
  };

  const handleOptionForTreatmentChange = (e: ChangeEvent<HTMLInputElement>, key: keyof TreatmentOption) => {
    modifyEntity(hierarchyId, (entity) => {
      const article = entity as MobileArticleItem;
      const isChecked = e.target.checked;
      const breakingTreatment = availableTreatmentsForSize.find((t) => t.key.includes('BREAKING'));
      const standardTreatment = availableTreatmentsForSize.find((t) => t.key.includes('STANDARD'));
      // @ts-expect-error - TS doesn't like this, but it's fine
      article.attributes[key as keyof MobileArticleAttributes] = isChecked;

      // Special handling for showFlashline: turn off redFlashline and centerText when flashline is turned off
      // Note: showFlashline is only editable on articles (LC has it always on), so only redFlashline drives treatment here
      if (key === 'showFlashline' && !isChecked) {
        if (article.attributes.redFlashline) {
          article.attributes.redFlashline = false;
          if (standardTreatment) article.attributes.treatment = standardTreatment.key;
        }
        if (article.attributes.centerText) article.attributes.centerText = false;
      }

      // Special handling for centerText: treatment changes only apply to LC (not articles)
      if (key === 'centerText' && contentType === 'LC') {
        if (isChecked) {
          if (breakingTreatment) {
            article.attributes.treatment = breakingTreatment.key;
          }
        } else if (standardTreatment) {
          article.attributes.treatment = standardTreatment.key;
        }
      }

      // Special handling for redFlashline: also update treatment for backwards compatibility
      if (key === 'redFlashline') {
        if (isChecked) {
          // Turning on red flashline should also force flashline on
          article.attributes.showFlashline = true;
          if (breakingTreatment) {
            article.attributes.treatment = breakingTreatment.key;
          }
        } else if (standardTreatment) {
          article.attributes.treatment = standardTreatment.key;
        }
      }
    });
  };

  if (hasErrorInConfig) {
    return null;
  }

  const handleUpdateVisual = (value: string): void => {
    modifyEntity(hierarchyId, (entity) => {
      const article = entity as MobileArticleItem;
      article.attributes.visual = value;
    });
  };

  return (
    <>
      {/* Treatment type radio buttons - Hidden but logic preserved for backwards compatibility */}
      {ENABLE_TREATMENTS && (
        <>
          <TreatmentOptions
            article={article}
            availableTreatmentsForSize={availableTreatmentsForSize}
            onTreatmentChange={handleTreatmentChange}
            isReadOnly={isReadOnly}
          />
          <Divider margin={{ top: 'md', bottom: 'md' }} fullWidth />
        </>
      )}
      {/* Toggles for the treatment type and content type */}
      <OptionsForTreatment
        article={article}
        availableOptionsForTreatment={availableOptionsForTreatment}
        onOptionForTreatmentChange={handleOptionForTreatmentChange}
        onVisualOptionChange={handleUpdateVisual}
        isReadOnly={isReadOnly}
      />
    </>
  );
};

const VisualOption = {
  PRIMARY: 'primary',
  HIDDEN: 'hidden',
  SECONDARY: 'secondary'
};

interface BreakingNewsOptionsProps {
  article: MobileArticleItem;
  options: TreatmentOption[];
  onOptionForTreatmentChange: (e: ChangeEvent<HTMLInputElement>, key: keyof TreatmentOption) => void;
  isReadOnly?: boolean;
}

const BreakingNewsOptions: FC<BreakingNewsOptionsProps> = ({
  article,
  options,
  onOptionForTreatmentChange,
  isReadOnly
}) => (
  <Wrapper className={style.breakingNewsSection}>
    <p className={style.sectionLabel}>Major/Breaking News</p>
    <Wrapper className={style.breakingNewsToggles}>
      {options.map((option, index) => {
        const key = Object.keys(option).at(0) as keyof typeof option;
        const toggleObj = option[key]!;

        const label = key === 'redFlashline' ? 'Red flashline' : key === 'centerText' ? 'Centered text' : '';

        const checked = article.attributes[key as keyof MobileArticleAttributes] ?? toggleObj.defaultValue;

        return (
          <Wrapper key={`${key}-${index}`}>
            <FormLabel label={label} labelPosition="right">
              <Switch
                disabled={toggleObj.disabled || isReadOnly}
                checked={checked as boolean}
                onChange={(e: ChangeEvent<HTMLInputElement>) => onOptionForTreatmentChange(e, key)}
              />
            </FormLabel>
          </Wrapper>
        );
      })}
    </Wrapper>
  </Wrapper>
);

interface OptionsForTreatmentProps {
  availableOptionsForTreatment: TreatmentConfig | null | undefined;
  article: MobileArticleItem;
  onOptionForTreatmentChange: (e: ChangeEvent<HTMLInputElement>, key: keyof TreatmentOption) => void;
  onVisualOptionChange?: (value: string) => void;
  isReadOnly?: boolean;
}

const OptionsForTreatment: FC<OptionsForTreatmentProps> = ({
  article,
  availableOptionsForTreatment,
  onOptionForTreatmentChange,
  onVisualOptionChange,
  isReadOnly
}) => {
  const options = availableOptionsForTreatment?.options ?? [];

  // Separate breaking news options from regular options
  const breakingNewsKeys = ['redFlashline', 'centerText'];
  const regularOptions = options.filter((option) => {
    const key = Object.keys(option).at(0);
    return key && !breakingNewsKeys.includes(key);
  });
  const breakingNewsOptions = options.filter((option) => {
    const key = Object.keys(option).at(0);
    return key && breakingNewsKeys.includes(key);
  });

  return (
    <>
      {/* Regular options (flashline, visual, etc.) */}
      {regularOptions.map((option, index) => {
        const key = Object.keys(option).at(0) as keyof typeof option;

        const toggleObj = option[key]!;

        if (key === 'visual') {
          return (
            <VisualOptions
              onVisualOptionChange={onVisualOptionChange}
              key={`${key}-${index}`}
              article={article}
              config={toggleObj}
              isReadOnly={isReadOnly}
            />
          );
        }

        // Custom labels for specific toggles
        const label = camelCaseToWords(key as string).replace('Show', '');

        const attributeValue = article.attributes[key as keyof MobileArticleAttributes];
        const checked =
          typeof attributeValue === 'boolean'
            ? attributeValue
            : typeof toggleObj.defaultValue === 'boolean'
            ? toggleObj.defaultValue
            : false;

        const isDisabled = key === 'showFlashline' ? false : toggleObj.disabled;

        return (
          <Wrapper key={`${key}-${index}`}>
            <FormLabel label={label} labelPosition="right">
              <Switch
                disabled={isDisabled || isReadOnly}
                checked={checked}
                onChange={(e: ChangeEvent<HTMLInputElement>) => onOptionForTreatmentChange(e, key)}
              />
            </FormLabel>
          </Wrapper>
        );
      })}

      {/* Divider and Breaking News section */}
      {breakingNewsOptions.length > 0 && (
        <>
          <Divider margin={{ top: 'md', bottom: 'md' }} fullWidth />
          <BreakingNewsOptions
            article={article}
            options={breakingNewsOptions}
            onOptionForTreatmentChange={onOptionForTreatmentChange}
            isReadOnly={isReadOnly}
          />
        </>
      )}
    </>
  );
};

interface TreatmentOptionsProps {
  availableTreatmentsForSize: {
    options?: TreatmentOption[] | null | undefined;
    label?: string | null | undefined;
    autoProperties?: { [key: string]: string | boolean } | null | undefined;
    key: string;
  }[];
  article: MobileArticleItem;
  onTreatmentChange: (e: ChangeEvent<HTMLInputElement>, treatmentOption: TreatmentConfig) => void;
  isReadOnly?: boolean;
}

const TreatmentOptions: FC<TreatmentOptionsProps> = ({
  article,
  availableTreatmentsForSize,
  onTreatmentChange,
  isReadOnly
}) => (
  <>
    {availableTreatmentsForSize.map((treatmentOption) => (
      <Wrapper key={treatmentOption.key}>
        <FormLabel label={treatmentOption.label} labelPosition="right" fullWidth>
          <Radio
            checked={treatmentOption.key === article.attributes.treatment}
            value={treatmentOption.key}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onTreatmentChange(e, treatmentOption)}
            disabled={isReadOnly}
          />
        </FormLabel>
      </Wrapper>
    ))}
  </>
);

interface VisualOptionsProps {
  article: MobileArticleItem;
  config: TreatmentOptionConfig;
  onVisualOptionChange?: (value: string) => void;
  isReadOnly?: boolean;
}

const VisualOptions: FC<VisualOptionsProps> = ({ article, config, onVisualOptionChange, isReadOnly }) => {
  const handleVisualChange = (e: ChangeEvent<HTMLInputElement>, value: string) => {
    if (value === VisualOption.PRIMARY || value === VisualOption.SECONDARY) {
      onVisualOptionChange?.(e.target.checked ? value : VisualOption.HIDDEN);
    }
  };

  return (
    <Wrapper className={style.visualOptionSwitches}>
      {config.values
        .filter((value) => value !== 'hidden')
        .map((value) => {
          const stringValue = String(value);
          return (
            <FormLabel label="Visual" labelPosition="right" key={stringValue}>
              <Switch
                checked={article.attributes.visual === value}
                value={stringValue}
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleVisualChange(e, stringValue)}
                disabled={config.disabled || isReadOnly}
              />
            </FormLabel>
          );
        })}
    </Wrapper>
  );
};

export default MobileArticleActions;
